/**
 * AppSettings — global shared context for editable master-data entities.
 *
 * Why: settings pages mutate branches, tax rates, units, dealer areas and the
 * company profile. Without a shared store, every page re-initialises from the
 * seeded mock arrays and sees stale data. This context is the single source of
 * truth for each entity.
 *
 * Persistence
 * ───────────
 * - When InstantDB is configured (VITE_INSTANT_APP_ID set), all 5 entities
 *   read live from InstantDB and writes flow through `db.transact(...)` with
 *   an optimistic UI overlay. Array entities go through `useEntityList`,
 *   which already implements the dual-backed pattern. The companyProfile
 *   singleton uses an inline `db.useQuery` + `db.transact` helper because
 *   it's a single-row entity rather than a list.
 * - When InstantDB is NOT configured, all 5 entities fall back to
 *   localStorage via demoStore — the original behaviour.
 *
 * Usage
 * ─────
 *   const { branches, updateBranches } = useAppSettings();
 *
 * The context is provided at the /_app layout level (see routes/_app.tsx).
 *
 * Persistence keys (all under "demo:" namespace via demoStore, AND the
 * matching InstantDB entity names):
 *   "companyProfile"  →  CompanyProfile      (singleton, _id = "default")
 *   "branches"        →  BranchRow[]
 *   "taxRates"        →  SharedTaxRateRow[]
 *   "units"           →  SharedUnitRow[]
 *   "dealerAreas"     →  MockDealerArea[]
 */

import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from "react";

import {
  DEFAULT_PREFIXES,
  type DocumentPrefixes,
} from "./documentTypes";

import { demoStore } from "./demoStore";
import { mockBranches, type MockBranch } from "./inventoryMock";
import {
  mockTaxRates,
  mockUnits,
  type MockTaxRate,
  type MockUnit,
} from "./catalogMock";
import { mockDealerAreas, type MockDealerArea } from "./dealerAreasMock";
import { useEntityList } from "./db/useEntityList";
import { getDb, isInstantDbEnabled } from "./db/client";
import { decodeEntity, encodeEntity } from "./db/codec";

/* ─────────────────────────────────────────────────────────────
 * Shared row types
 *
 * Settings pages augment the raw mock types with operational fields
 * (deletedAt, decimalPlaces, etc.).  These types are exported so every
 * consumer (settings pages + any read-only consumers) share the same shape.
 * ───────────────────────────────────────────────────────────── */

export type BranchRow = MockBranch & { deletedAt: number | null };

export type SharedTaxRateRow = MockTaxRate & {
  jurisdiction: string;
  deletedAt: number | null;
};

export type SharedUnitRow = MockUnit & {
  decimalPlaces: number;
  isArchived: boolean;
};

// MockDealerArea already carries deletedAt — re-exported as a convenience alias.
export type { MockDealerArea };

export type CompanyProfile = {
  legalName: string;
  displayName: string;
  baseCurrency: string;
  fiscalYearStartMonth: number;
  addressLine1: string;
  addressCity: string;
  addressCountry: string;
  taxRegistrationNumber: string;
  websiteUrl: string;
  contactEmail: string;
  contactPhone: string;
  /** Invoice number prefix, e.g. "INV", "AC". */
  invoicePrefix: string;
  /** Year format in invoice numbers: "full" = 2026, "short" = 26. */
  invoiceYearFormat: "full" | "short";
  /** Zero-pad length for the counter portion of invoice numbers. */
  invoicePadLength: number;
  /**
   * Default VAT rate applied when `invoices.vatEnabled` is true.
   * Stored in E4 scaling (e.g. BigInt(150000) = 15%).
   */
  defaultVatRatePercentE4: bigint;
  /** Configurable prefixes for all document types (invoice, SO, PO, CN, payment, expense). */
  documentPrefixes: DocumentPrefixes;
};

/** Fixed legacy `_id` used for the singleton companyProfile row. */
const COMPANY_PROFILE_ID = "default";
const COMPANY_PROFILE_ENTITY = "companyProfile" as const;

/* ─────────────────────────────────────────────────────────────
 * Helpers
 * ───────────────────────────────────────────────────────────── */

function inferDecimalPlaces(unit: MockUnit): number {
  if (unit.dimension === "count") return 0;
  if (unit.dimension === "mass" || unit.dimension === "volume") return 2;
  if (unit.dimension === "length") return 1;
  return 0;
}

/* ─────────────────────────────────────────────────────────────
 * Seeds — built once from mock arrays
 * ───────────────────────────────────────────────────────────── */

export const SEED_COMPANY_PROFILE: CompanyProfile = {
  legalName: "",
  displayName: "",
  baseCurrency: "BDT",
  fiscalYearStartMonth: 1,
  addressLine1: "",
  addressCity: "",
  addressCountry: "",
  taxRegistrationNumber: "",
  websiteUrl: "",
  contactEmail: "",
  contactPhone: "",
  invoicePrefix: "",
  invoiceYearFormat: "full",
  invoicePadLength: 6,
  defaultVatRatePercentE4: 0n,
  documentPrefixes: { ...DEFAULT_PREFIXES },
};

const SEED_BRANCHES: BranchRow[] = mockBranches.map((b) => ({
  ...b,
  deletedAt: null,
}));

const SEED_TAX_RATES: SharedTaxRateRow[] = mockTaxRates.map((r) => ({
  ...r,
  jurisdiction: "Bangladesh",
  deletedAt: null,
}));

/**
 * Default units for a Bangladesh animal-health products business.
 *
 * `mockUnits` from catalogMock is empty (emptyIfInstant returns []), so we
 * hardcode a sensible default list here so the units dropdown is never empty
 * on first load.
 */
const HARDCODED_UNITS: MockUnit[] = [
  { _id: "unit_pcs", code: "pcs", displayName: "Pieces", dimension: "count", conversionFactor: 1, isActive: true },
  { _id: "unit_scratch", code: "scratch", displayName: "Scratches", dimension: "count", conversionFactor: 1, isActive: true },
  { _id: "unit_pkg", code: "pkg", displayName: "Package", dimension: "count", conversionFactor: 1, isActive: true },
  { _id: "unit_kg", code: "kg", displayName: "Kilogram", dimension: "mass", conversionFactor: 1, isActive: true },
  { _id: "unit_box", code: "box", displayName: "Boxes", dimension: "count", conversionFactor: 1, isActive: true },
  { _id: "unit_bottle", code: "bottle", displayName: "Bottle", dimension: "count", conversionFactor: 1, isActive: true },
  { _id: "unit_liter", code: "L", displayName: "Liter", dimension: "volume", conversionFactor: 1, isActive: true },
  { _id: "unit_ussal", code: "ussal", displayName: "Ussal", dimension: "count", conversionFactor: 1, isActive: true },
  { _id: "unit_pouch", code: "pouch", displayName: "Pouch", dimension: "count", conversionFactor: 1, isActive: true },
  { _id: "unit_bag", code: "bag", displayName: "Bag", dimension: "count", conversionFactor: 1, isActive: true },
  { _id: "unit_set", code: "set", displayName: "Set", dimension: "count", conversionFactor: 1, isActive: true },
  { _id: "unit_dozen", code: "dz", displayName: "Dozen", dimension: "count", conversionFactor: 12, isActive: true },
  { _id: "unit_ml", code: "mL", displayName: "Milliliter", dimension: "volume", conversionFactor: 0.001, isActive: true },
  { _id: "unit_g", code: "g", displayName: "Gram", dimension: "mass", conversionFactor: 0.001, isActive: true },
];

const SEED_UNITS: SharedUnitRow[] = (
  mockUnits.length > 0 ? mockUnits : HARDCODED_UNITS
).map((u) => ({
  ...u,
  decimalPlaces: inferDecimalPlaces(u),
  isArchived: false,
}));

const SEED_DEALER_AREAS: MockDealerArea[] = mockDealerAreas.map((a) => ({
  ...a,
}));

/* ─────────────────────────────────────────────────────────────
 * Context shape
 * ───────────────────────────────────────────────────────────── */

type AppSettingsCtx = {
  /** Live company profile — reflects Settings → Company edits instantly. */
  companyProfile: CompanyProfile;
  /** Live branches list — persists across navigation. */
  branches: BranchRow[];
  /** Live tax rates — persists across navigation. */
  taxRates: SharedTaxRateRow[];
  /** Live units — persists across navigation. */
  units: SharedUnitRow[];
  /** Live dealer areas — persists across navigation. */
  dealerAreas: MockDealerArea[];

  updateCompanyProfile: (profile: CompanyProfile) => void;
  updateBranches: (updater: (prev: BranchRow[]) => BranchRow[]) => void;
  updateTaxRates: (
    updater: (prev: SharedTaxRateRow[]) => SharedTaxRateRow[],
  ) => void;
  updateUnits: (updater: (prev: SharedUnitRow[]) => SharedUnitRow[]) => void;
  updateDealerAreas: (
    updater: (prev: MockDealerArea[]) => MockDealerArea[],
  ) => void;
};

const AppSettingsContext = createContext<AppSettingsCtx | null>(null);

/* ─────────────────────────────────────────────────────────────
 * Singleton state hook for companyProfile
 *
 * Same dual-backed routing as useEntityList, but tailored for a single
 * row entity:
 *   - InstantDB path: useQuery the entity, find the row with
 *     _id === "default", fall back to seed if missing; setter performs
 *     a single db.transact update keyed by COMPANY_PROFILE_ID.
 *   - Local path: useState + demoStore (original behaviour).
 *
 * Routing is stable for the lifetime of any component instance, so calling
 * one branch's hooks vs the other is consistent across renders.
 * ───────────────────────────────────────────────────────────── */

function useCompanyProfileState(
  seed: CompanyProfile,
): [CompanyProfile, (next: CompanyProfile) => void] {
  const useInstant = isInstantDbEnabled();

  // The routing decision is stable per component instance — env var and
  // seed shape never change mid-session — so it's safe to call one branch's
  // hooks vs the other consistently across renders.
  // eslint-disable-next-line react-hooks/rules-of-hooks
  return useInstant
    ? useInstantCompanyProfile(seed)
    : // eslint-disable-next-line react-hooks/rules-of-hooks
      useLocalCompanyProfile(seed);
}

function useLocalCompanyProfile(
  seed: CompanyProfile,
): [CompanyProfile, (next: CompanyProfile) => void] {
  const [profile, setProfile] = useState<CompanyProfile>(() =>
    demoStore.get(COMPANY_PROFILE_ENTITY, seed),
  );
  const setter = useCallback((next: CompanyProfile) => {
    setProfile(next);
    demoStore.set(COMPANY_PROFILE_ENTITY, next);
  }, []);
  return [profile, setter];
}

function useInstantCompanyProfile(
  seed: CompanyProfile,
): [CompanyProfile, (next: CompanyProfile) => void] {
  // getDb() returns a live client because isInstantDbEnabled() is true.
  const db = getDb()!;

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const queryResult = (db.useQuery as any)({ companyProfile: {} }) as {
    data?: { companyProfile?: Array<Record<string, unknown>> };
    error?: { message: string };
  };
  const rows = queryResult?.data?.companyProfile;

  // Optimistic overlay — held until the live query result lands.
  const [overlay, setOverlay] = useState<CompanyProfile | null>(null);

  const profile = useMemo<CompanyProfile>(() => {
    if (overlay) return overlay;
    if (!Array.isArray(rows) || rows.length === 0) return seed;
    const match =
      rows.find((r) => r._id === COMPANY_PROFILE_ID) ?? rows[0];
    if (!match) return seed;
    // companyProfile has no BigInt fields, so decode is a near-no-op
    // pass-through — but we route through it for consistency with the rest
    // of the dual-backed layer.
    const decoded = decodeEntity<Record<string, unknown>>(
      COMPANY_PROFILE_ENTITY,
      match,
    );
    // Strip the wire-only fields (id, _id) before exposing to consumers —
    // the CompanyProfile type doesn't include them.
    const clean: Record<string, unknown> = { ...decoded };
    delete clean.id;
    delete clean._id;
    return clean as CompanyProfile;
  }, [overlay, rows, seed]);

  // Clear the overlay once a new query result arrives — the optimistic
  // value has been replaced by the canonical server value.
  // We intentionally do NOT depend on `profile` here; we want to clear
  // the overlay only when the upstream `rows` reference changes.
  useEffect(() => {
    setOverlay(null);
  }, [rows]);

  const setter = useCallback(
    (next: CompanyProfile) => {
      setOverlay(next);

      const encoded = encodeEntity(COMPANY_PROFILE_ENTITY, {
        ...next,
        _id: COMPANY_PROFILE_ID,
      });
      // The `id` (UUID) is the transact key, not a body field.
      delete encoded.id;

      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      const tx = (db.tx as any)[COMPANY_PROFILE_ENTITY][
        COMPANY_PROFILE_ID
      ].update(encoded);

      db.transact([tx]).catch((err: unknown) => {
        console.error(
          `[appSettings] companyProfile transact failed:`,
          err,
        );
      });
    },
    [db],
  );

  return [profile, setter];
}

/* ─────────────────────────────────────────────────────────────
 * Provider — mount at the _app layout level
 * ───────────────────────────────────────────────────────────── */

export function AppSettingsProvider({ children }: { children: ReactNode }) {
  // companyProfile is a singleton — routed via the dedicated hook below.
  const [companyProfile, setCompanyProfile] =
    useCompanyProfileState(SEED_COMPANY_PROFILE);

  // The four array entities use useEntityList — the same dual-backed
  // hook used everywhere else in the app. When InstantDB is wired,
  // it returns the live query result regardless of the seed; the seed
  // only matters for the localStorage fallback.
  const [branches, setBranchesRaw] = useEntityList<BranchRow[]>(
    "branches",
    SEED_BRANCHES,
  );
  const [taxRates, setTaxRatesRaw] = useEntityList<SharedTaxRateRow[]>(
    "taxRates",
    SEED_TAX_RATES,
  );
  const [units, setUnitsRaw] = useEntityList<SharedUnitRow[]>(
    "units",
    SEED_UNITS,
  );
  const [dealerAreas, setDealerAreasRaw] = useEntityList<MockDealerArea[]>(
    "dealerAreas",
    SEED_DEALER_AREAS,
  );

  const updateCompanyProfile = useCallback(
    (profile: CompanyProfile) => {
      setCompanyProfile(profile);
    },
    [setCompanyProfile],
  );

  const updateBranches = useCallback(
    (updater: (prev: BranchRow[]) => BranchRow[]) => {
      setBranchesRaw((prev) => updater(prev));
    },
    [setBranchesRaw],
  );

  const updateTaxRates = useCallback(
    (updater: (prev: SharedTaxRateRow[]) => SharedTaxRateRow[]) => {
      setTaxRatesRaw((prev) => updater(prev));
    },
    [setTaxRatesRaw],
  );

  const updateUnits = useCallback(
    (updater: (prev: SharedUnitRow[]) => SharedUnitRow[]) => {
      setUnitsRaw((prev) => updater(prev));
    },
    [setUnitsRaw],
  );

  const updateDealerAreas = useCallback(
    (updater: (prev: MockDealerArea[]) => MockDealerArea[]) => {
      setDealerAreasRaw((prev) => updater(prev));
    },
    [setDealerAreasRaw],
  );

  return (
    <AppSettingsContext.Provider
      value={{
        companyProfile,
        branches,
        taxRates,
        units,
        dealerAreas,
        updateCompanyProfile,
        updateBranches,
        updateTaxRates,
        updateUnits,
        updateDealerAreas,
      }}
    >
      {children}
    </AppSettingsContext.Provider>
  );
}

/* ─────────────────────────────────────────────────────────────
 * Hook
 * ───────────────────────────────────────────────────────────── */

export function useAppSettings(): AppSettingsCtx {
  const ctx = useContext(AppSettingsContext);
  if (!ctx) {
    throw new Error(
      "useAppSettings must be used inside <AppSettingsProvider>. " +
        "Make sure the /_app layout wraps children with AppSettingsProvider.",
    );
  }
  return ctx;
}
